export type EmissionType = 'default' | 'mec'

export const MEC_MAX_WORKLOAD_HOURS = 360

export interface EmissionPurchaseOrderItem {
  item_type?: string | null
  order_status?: string | null
}

export function hasPaidEmissionOrderItem(
  orderItems: EmissionPurchaseOrderItem[] | undefined
): boolean {
  return (
    orderItems?.some(
      item => item.item_type === 'emission' && item.order_status === 'paid'
    ) ?? false
  )
}

/**
 * Returns the emission type that should drive purchase UI and checkout.
 * After a refund, enrolment may still store `mec` from the previous order.
 */
export function getEmissionTypeForPurchase(enrolment: {
  emission_type: EmissionType
  order_items?: EmissionPurchaseOrderItem[]
}): EmissionType {
  if (hasPaidEmissionOrderItem(enrolment.order_items)) {
    return enrolment.emission_type
  }

  return 'default'
}

export function shouldOfferMecCertificateOption({
  hasMec,
  workload,
  emissionTypeForPurchase,
}: {
  hasMec: boolean
  workload: string | number
  emissionTypeForPurchase: EmissionType
}): boolean {
  return (
    hasMec && emissionTypeForPurchase === 'default' && Number(workload) < MEC_MAX_WORKLOAD_HOURS
  )
}

/**
 * Vitrine (course details, pre-purchase): whether the "Nosso Certificado" tab
 * should show the MEC seal fee disclosure. Courses of exactly/only 360h are
 * the MEC-free "Curso Livre" modality (see course-information/faq.tsx) and
 * must never mention the fee, FAMEC or INCI Brasil in that context.
 */
export function shouldShowMecFeeDisclosure({
  hasMec,
  workloads,
}: {
  hasMec: boolean | undefined
  workloads: { workload_value: string }[] | undefined
}): boolean {
  if (!hasMec) {
    return false
  }

  return (
    workloads?.some(
      workload => Number(workload.workload_value) < MEC_MAX_WORKLOAD_HOURS
    ) ?? false
  )
}

export function shouldShowMecAddonInCheckout(item: {
  enrolment_emission_type: string
  enrolment_workload: string
  item_type: string
}): boolean {
  if (item.item_type === 'printing') {
    return false
  }

  if (Number(item.enrolment_workload) >= MEC_MAX_WORKLOAD_HOURS) {
    return false
  }

  return (
    item.enrolment_emission_type === 'default' ||
    item.enrolment_emission_type === 'mec'
  )
}

export function shouldResetEmissionTypeOnAddToCart({
  withMec,
  storedEmissionType,
  orderItems,
}: {
  withMec: boolean
  storedEmissionType: EmissionType
  orderItems?: EmissionPurchaseOrderItem[]
}): boolean {
  return (
    !withMec &&
    storedEmissionType === 'mec' &&
    !hasPaidEmissionOrderItem(orderItems)
  )
}
